// A single-choice control drawn as a row of chips. Extracted from
// `screens/analytics/RangeChips.tsx`, whose own header nominated itself: *"if
// a second screen needs chips, this is the component to promote into `ui/`."*
// Four call sites now need it — the time range, the corpus picker, the clock's
// granularity, and the clock's cycle — so it earned the promotion.
//
// A DISABLED OPTION STILL DRAWS, AND CARRIES ITS REASON. That is the whole
// reason this is not just three `Button`s: the clock offers a minute step it
// refuses to serve, and hiding it would leave a customer wondering whether the
// product can do it at all. Drawn-but-refused, with the measurement behind the
// refusal on the control itself, answers the question instead of raising it.
//
// The Figma file ships no Chip component — every chip drawn there is a Button
// instance — so the archetype is authored rather than ported, exactly as
// `MeterBar` was.

import { Button } from "./Button";
import "./ui.css";

export type ChipOption<T extends string | number> = {
  value: T;
  label: string;
  /** Refused rather than hidden. `why` rides a `title` AND a rendered
   *  paragraph below the row — see the note at the bottom of this file for
   *  why the tooltip alone is not enough. */
  disabled?: boolean;
  /** Required whenever `disabled` — a refusal with no reason is a dead end. */
  why?: string;
};

export type ChipGroupProps<T extends string | number> = {
  /** Names the group for assistive tech. Never rendered. */
  label: string;
  options: ChipOption<T>[];
  value: T;
  onChange: (next: T) => void;
};

export function ChipGroup<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: ChipGroupProps<T>) {
  return (
    <div className="maisight-ui-chips" role="group" aria-label={label}>
      {options.map((option) => (
        <Button
          key={String(option.value)}
          size="sm"
          variant={option.value === value ? "primary" : "secondary"}
          aria-pressed={option.value === value}
          disabled={option.disabled}
          title={option.why}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
      {/* The reason travels in the DOM, not only in a tooltip: a `title` is
       *  invisible to touch and to most screen readers. */}
      {options
        .filter((option) => option.disabled && option.why)
        .map((option) => (
          <p
            key={`why-${option.value}`}
            className="maisight-ui-chips-why type-bodyS"
            data-measured="no"
          >
            {option.why}
          </p>
        ))}
    </div>
  );
}
